import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { ToastContainerComponent } from '@core/layout/toast-container/toast-container.component';
import { FaqWidgetComponent } from '@features/faq/faq-widget/faq-widget.component';
import { ReleaseNotesModalComponent } from '@shared/components/release-notes-modal/release-notes-modal.component';
import { AuthService } from '@core/auth/auth.service';
import { ReleaseNotesService } from '@core/services/release-notes.service';
import { VersionService } from '@core/services/version.service';
import { ToastService } from '@core/services/toast.service';

/**
 * Échec de chargement d'un morceau de l'application (page chargée à la demande) :
 * typiquement une ancienne version restée ouverte dont les fichiers ont été
 * supprimés au déploiement. Messages Chrome, Safari et Firefox.
 */
const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed/i;

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastContainerComponent, FaqWidgetComponent, ReleaseNotesModalComponent],
  template: `
    <app-toast-container></app-toast-container>
    <router-outlet></router-outlet>
    <app-faq-widget></app-faq-widget>
    <app-release-notes-modal></app-release-notes-modal>
  `,
  styles: []
})
export class AppComponent implements OnInit {
  title = 'Majelan Assistant IA';

  private readonly authService = inject(AuthService);
  private readonly releaseNotes = inject(ReleaseNotesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly versionService = inject(VersionService);
  private readonly toast = inject(ToastService);

  /** Retour de l'application au premier plan (iPhone : page restée en mémoire). */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      void this.versionService.recheck();
    }
  }

  ngOnInit(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      // Nouvelle version déployée : rechargement une fois le changement de page
      // accepté (gardes de sortie de page passées, jamais au milieu d'une saisie).
      if (event instanceof NavigationEnd && this.versionService.updateAvailable) {
        this.versionService.reloadTo(event.urlAfterRedirects);
      }
      // Fichier de l'ancienne version introuvable : rechargement sur la page demandée.
      if (event instanceof NavigationError && CHUNK_LOAD_ERROR.test(String(event.error?.message ?? event.error))
          && !this.versionService.reloadTo(event.url)) {
        this.toast.warning("Une nouvelle version de l'application est disponible : rechargez la page.", 8000);
      }
    });

    // Charge les notes non lues à chaque (ré)authentification, pour gérer
    // aussi bien le rechargement de page (utilisateur déjà connecté) que
    // le login en cours de session.
    this.authService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user) {
        this.releaseNotes.refreshUnread().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
      } else {
        this.releaseNotes.clear();
      }
    });
  }
}
